/*
 * The single source of truth for the walkthrough. It drives the routes, the
 * top nav, the Contents drawer, the step bar and the product sidebar.
 *
 * `component` is a getter so that a page module may import this file (for
 * PRODUCTS, say) without the import cycle leaving `component` undefined.
 */
import {
  BadgeCheck,
  ChartColumn,
  Building2,
  ClipboardCheck,
  FileText,
  Gauge,
  Hospital,
  Layers,
  ListChecks,
  MessageCircle,
  MessagesSquare,
  Smartphone,
  TrendingUp,
} from 'lucide-react';

import PatientCost from '../pages/problem/PatientCost';
import BookingDoubts from '../pages/problem/BookingDoubts';
import PatientsResearch from '../pages/shift/PatientsResearch';
import PatientJourney from '../pages/shift/PatientJourney';
import Intro from '../pages/product/Intro';
import BrandEquity from '../pages/product/BrandEquity';
import Hra from '../pages/product/Hra';
import SmartReports from '../pages/product/SmartReports';
import VizApp from '../pages/product/VizApp';
/*
 * AI Video is out of the flow for now (Harsh, 2026-10-17). Its page,
 * src/pages/product/AiVideo.jsx (+ .css), stays on disk, unimported. To bring it
 * back: import it here, add its step after Viz App in STEPS (steps are numbered
 * by their position), and add its PRODUCTS entry back after Viz App:
 *   step('/product/ai-video', 'product.ai-video', 'product', 'AI Video', () => AiVideo)
 *   product('/product/ai-video', 'AI Video', 'deliver', 'Their own report, as a narrated video.', Clapperboard)
 * (Clapperboard from lucide-react). The tests read STEPS.length and PRODUCTS.length.
 */
import HospitalReports from '../pages/product/HospitalReports';
import Corporate from '../pages/product/Corporate';
import HealthScores from '../pages/product/HealthScores';
import Recommendations from '../pages/product/Recommendations';
import Trends from '../pages/product/Trends';
import Lumi from '../pages/product/Lumi';
import Analytics from '../pages/product/Analytics';
import WhatsApp from '../pages/product/WhatsApp';
import IntelligenceLayer from '../pages/product/IntelligenceLayer';
import Levels from '../pages/packages/Levels';
import Clients from '../pages/packages/Clients';
import GettingLive from '../pages/GettingLive';
import NextStep from '../pages/NextStep';

export const SECTIONS = [
  { key: 'problem', label: 'Problem', route: '/problem/patient-cost' },
  { key: 'shift', label: 'Shift', route: '/shift/patients-research' },
  { key: 'product', label: 'Product', route: '/product' },
  { key: 'packages', label: 'Packages', route: '/packages' },
  // Shown as "Integration" (Harsh, 2026-10-17); the key, route and IDs keep "getting-live".
  { key: 'getting-live', label: 'Integration', route: '/getting-live' },
  { key: 'next-step', label: 'Next step', route: '/next-step' },
];

function step(route, screenId, section, title, getComponent) {
  return {
    route,
    screenId,
    section,
    title,
    get component() {
      return getComponent();
    },
  };
}

/*
 * The walkthrough, in order. `n` (1-based) comes from the position, so adding or
 * removing a step never needs a renumber; read STEPS.length, never a count.
 */
export const STEPS = [
  step('/problem/patient-cost', 'problem.patient-cost', 'problem', 'The cost of losing patients', () => PatientCost),
  step('/problem/booking-doubts', 'problem.booking-doubts', 'problem', 'Why they pick someone else', () => BookingDoubts),
  step('/shift/patients-research', 'shift.patients-research', 'shift', 'What it fixes for you', () => PatientsResearch),
  step('/shift/patient-journey', 'shift.patient-journey', 'shift', 'Be there at every step', () => PatientJourney),
  // The animated intro opens the Product section. The product index lives off
  // the walkthrough at PRODUCT_INDEX_ROUTE (see below).
  step('/product', 'product.intro', 'product', 'The engine', () => Intro),
  step('/product/brand-equity', 'product.brand-equity', 'product', 'Brand equity', () => BrandEquity),
  step('/product/hra', 'product.hra', 'product', 'HRA', () => Hra),
  step('/product/smart-reports', 'product.smart-reports', 'product', 'Smart Reports', () => SmartReports),
  step('/product/viz-app', 'product.viz-app', 'product', 'Viz App', () => VizApp),
  step('/product/hospital-reports', 'product.hospital-reports', 'product', 'Hospital reports', () => HospitalReports),
  step('/product/corporate', 'product.corporate', 'product', 'Corporate', () => Corporate),
  step('/product/health-scores', 'product.health-scores', 'product', 'Health scores', () => HealthScores),
  step('/product/recommendations', 'product.recommendations', 'product', 'Recommendations', () => Recommendations),
  step('/product/trends', 'product.trends', 'product', 'Trends', () => Trends),
  step('/product/lumi', 'product.lumi', 'product', 'AI chat', () => Lumi),
  step('/product/analytics', 'product.analytics', 'product', 'Analytics', () => Analytics),
  step('/product/whatsapp', 'product.whatsapp', 'product', 'WhatsApp', () => WhatsApp),
  step(
    '/product/intelligence-layer',
    'product.intelligence-layer',
    'product',
    'The intelligence layer',
    () => IntelligenceLayer,
  ),
  step('/packages', 'packages.levels', 'packages', 'Packages', () => Levels),
  step('/packages/clients', 'packages.clients', 'packages', 'Trusted by leading labs', () => Clients),
  // Shown as "Integration"; the route and the getting-live.* IDs stay.
  step('/getting-live', 'getting-live.integration', 'getting-live', 'Integration', () => GettingLive),
  step('/next-step', 'next-step.book-demo', 'next-step', 'Next step', () => NextStep),
].map((s, i) => {
  // Assigned, not spread: a spread would read the `component` getter too early.
  s.n = i + 1;
  return s;
});

/*
 * The product index (screen product.overview): every product in one grid. It
 * is not a step: no step bar, not in the nav or the Contents. The sidebar's
 * "All products" link opens it; App.js routes it inside the shell.
 */
export const PRODUCT_INDEX_ROUTE = '/product_hidden';

export const TOTAL_STEPS = STEPS.length;

export const STAGES = [
  { key: 'attract', label: 'Attract', number: 1 },
  { key: 'deliver', label: 'Deliver', number: 2 },
  { key: 'counsel', label: 'Counsel', number: 3 },
  { key: 'growth', label: 'Growth', number: 4 },
  { key: 'foundation', label: 'Foundation', number: null },
];

function product(route, name, stage, blurb, icon) {
  return { route, slug: route.split('/').pop(), name, stage, blurb, icon };
}

export const PRODUCTS = [
  product(
    '/product/brand-equity',
    'Brand equity',
    'attract',
    'Stand apart from every lab around you.',
    BadgeCheck,
  ),
  product(
    '/product/hra',
    'HRA',
    'attract',
    'A short health quiz that points each patient to the right package.',
    ClipboardCheck,
  ),
  product(
    '/product/smart-reports',
    'Smart Reports',
    'deliver',
    'The report you already send, made readable.',
    FileText,
  ),
  product(
    '/product/viz-app',
    'Viz App',
    'deliver',
    'The same report, opened and explored on the phone.',
    Smartphone,
  ),
  product(
    '/product/hospital-reports',
    'Hospital reports',
    'deliver',
    'The prescription and the discharge summary, explained.',
    Hospital,
  ),
  product(
    '/product/corporate',
    'Corporate',
    'deliver',
    "A workforce's health in one page, without anyone's name.",
    Building2,
  ),
  product(
    '/product/health-scores',
    'Health scores',
    'counsel',
    'One wellness score, and the risks that pulled it down.',
    Gauge,
  ),
  product(
    '/product/recommendations',
    'Recommendations',
    'counsel',
    'Clear next steps for every result.',
    ListChecks,
  ),
  product('/product/trends', 'Trends', 'counsel', 'Every result in context, over time.', TrendingUp),
  product(
    '/product/lumi',
    'AI chat',
    'counsel',
    "Answers patients' questions using their own report.",
    MessagesSquare,
  ),
  product(
    '/product/analytics',
    'Analytics',
    'growth',
    'Turn health data into better decisions.',
    ChartColumn,
  ),
  product(
    '/product/whatsapp',
    'WhatsApp',
    'growth',
    'Reports, reminders and nudges where patients already are.',
    MessageCircle,
  ),
  product(
    '/product/intelligence-layer',
    'The intelligence layer',
    'foundation',
    'The clinical knowledge base behind every product.',
    Layers,
  ),
];

function normalise(pathname) {
  if (!pathname) return '/';
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

export function getStepByRoute(pathname) {
  const route = normalise(pathname);
  return STEPS.find((s) => s.route === route);
}

export function getStepByNumber(n) {
  return STEPS.find((s) => s.n === n);
}
